import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { HttpStatusCode } from "axios";
import clsx from "clsx";

import {
  VOTE_ITEM_TYPE,
  VOTE_PERIOD,
  VOTE_STATUS,
} from "../../../../consts/hotColdVote";
import { MANAGER_ROLE } from "../../../../consts/member";

import { errorResponse } from "../../../../libs/common";
import useErrors from "../../../../libs/useErrors";

import { useUserContext } from "../../../../contexts/userContext";

import {
  BuildingFloorItem,
  BuildingSpaceDetail,
  BuildingSpaceItem,
  getBuildingFloorList,
  getBuildingSpaceDetail,
  getBuildingSpaceList,
} from "../../../../services/buildingServices";
import {
  TalkHotColdItem,
  getHotColdNotice,
} from "../../../../services/talkServices";
import {
  Vote,
  VoteItemType,
  VotePeriod,
  getVoteItemTypeLabel,
  getVoteStatusLabel,
} from "../../../../services/hotColdVoteServices";
import {
  getManagerHotColdVote,
  getManagerHotColdVoteBuildingSpace,
  GetManagerHotColdVoteBuildingSpaceRequest,
  ManagerVote,
  patchManagerHotColdVoteFeedback,
  postManagerHotColdVoteFeedback,
} from "../../../../services/managerHotColdVoteServices";
import {
  AirSpaceInfo,
  getMonitoringAirSpace,
} from "../../../../services/iotServices";
import {
  DifferentBuildingPopup,
  HotColdLabelWrapper,
  VoteButton,
} from "../../../Services/HotColdVote/Detail";

import { RefreshIcon } from "../../../../hooks/useStoreHeader";
import useSimplePopup from "../../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";

import { HotColdVoteLineChart } from "../../../../components/HotCold/LineChart";
import { HotColdVoteBarChart } from "../../../../components/HotCold/BarChart";
import { HotColdVoteRemainTime } from "../../../../components/HotCold/RemainTime";
import { Skeleton, TalkFindBuildingDownArrow } from "../../../Talk";
import HotColdSummaryList from "../../../../components/HotCold/HotColdSummaryList";
import HorizontalRule from "../../../../components/Rule/Horizontal";
import StickyHeader from "../../../../components/Layout/StickyHeader";
import CommonButton from "../../../../components/Button";
import CommonTextArea from "../../../../components/TextArea";
import CommonSelect, { SelectOption } from "../../../../components/Select";
import CommonPopup from "../../../../components/Popup";
import PinchZoomImage from "../../../../components/PinchZoomImage";
import SimpleScrollBar from "../../../../components/SimpleScrollBar";

import { HomeBuildingNoticeIcon, ViewNoticeLinkIcon } from "../../../View";

type Props = {};

export default function ManagerHotColdVoteDetail(props: Props) {
  const DASH = "-";

  const selectFloorRef = useRef<HTMLDivElement>(null);
  const selectSpaceRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const paramVoteId = Number(params.get("voteId"));
  const paramSpaceId = params.get("buildingSpaceId");

  const [user] = useUserContext();

  const [errors, { removeError }] = useErrors();
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isBuildingLoading, setIsBuildingLoading] = useState<boolean>(true);
  const [isVoteLoading, setIsVoteLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({
    isLoading: isLoading || isBuildingLoading || isVoteLoading,
  });

  const [vote, setVote] = useState<Partial<ManagerVote>>({});

  const [hotColdNotice, setHotColdNotice] = useState<TalkHotColdItem>();

  const [airSpaceInfo, setAirSpaceInfo] = useState<AirSpaceInfo | undefined>(
    undefined
  );

  const [popup, setPopup] = useState<"differentBuilding" | "">("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [spaceDetail, setSpaceDetail] = useState<
    BuildingSpaceDetail | undefined
  >(undefined);

  const [floorList, setFloorList] = useState<BuildingFloorItem[]>([]);
  const [spaceList, setSpaceList] = useState<BuildingSpaceItem[]>([]);

  const [selectedFloor, setSelectedFloor] = useState<
    BuildingFloorItem | undefined
  >();
  const [selectedSpace, setSelectedSpace] = useState<
    BuildingSpaceItem | undefined
  >();

  const timeOption: SelectOption[] = [
    { value: VOTE_PERIOD.AM, label: "오전" },
    { value: VOTE_PERIOD.PM, label: "오후" },
  ];
  const [selectedTime, setSelectedTime] = useState<SelectOption>(timeOption[0]);

  const getTextColor = (voteItemType?: VoteItemType) => {
    if (!voteItemType) return "";

    return voteItemType === VOTE_ITEM_TYPE.HOT
      ? "text-[#EB3F59]"
      : voteItemType === VOTE_ITEM_TYPE.COLD
      ? "text-[#0A78F2]"
      : "text-[#3C6578]";
  };

  const loadNotice = async () => {
    if (!user.buildingId) return;

    try {
      const res = await getHotColdNotice({ buildingId: user.buildingId });
      if (res.status === HttpStatusCode.Ok) {
        setHotColdNotice(res.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const loadFloor = async () => {
    if (!user.buildingId) return;

    try {
      const resFloor = await getBuildingFloorList({
        buildingId: user.buildingId,
      });
      if (resFloor.status === HttpStatusCode.Ok) {
        setFloorList(resFloor.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const loadSpaceDetail = async ({
    buildingSpaceId,
    params,
    isLoadVote = false,
  }: {
    buildingSpaceId: string;
    params?: GetManagerHotColdVoteBuildingSpaceRequest;
    isLoadVote?: boolean;
  }) => {
    setIsLoading(true);
    Promise.allSettled([
      getBuildingSpaceDetail({ buildingSpaceId }),
      getManagerHotColdVoteBuildingSpace(buildingSpaceId, {
        voteDate: params?.voteDate,
        votePeriod: params?.votePeriod,
      }),
      getMonitoringAirSpace({ buildingSpaceId }),
    ])
      .then((ress) => {
        if (
          ress[0].status === "fulfilled" &&
          ress[0].value.status === HttpStatusCode.Ok
        ) {
          setSpaceDetail(ress[0].value.data);
        }
        if (
          ress[1].status === "fulfilled" &&
          ress[1].value.status === HttpStatusCode.Ok
        ) {
          const data = ress[1].value.data;

          if (isLoadVote) {
            loadVote({ voteId: data.vote?.voteId, ...data });
          }
        }
        if (
          ress[2].status === "fulfilled" &&
          ress[2].value.status === HttpStatusCode.Ok
        ) {
          setAirSpaceInfo(ress[2].value.data);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const loadVote = async (
    vote: Partial<Vote>,
    showIsLoading: boolean = true
  ) => {
    if (showIsLoading) setIsVoteLoading(true);

    try {
      if (vote?.voteId) {
        const res = await getManagerHotColdVote({ voteId: vote.voteId });

        if (res.status === HttpStatusCode.Ok) {
          const {
            votePeriod,
            buildingFloorId,
            buildingFloorName: floorName,
            buildingSpaceId,
            buildingSpaceName: spaceName,
          } = res.data;

          setVote(res.data);
          setSelectedTime(
            timeOption.find((item) => item.value === votePeriod) ??
              timeOption[0]
          );
          setSelectedFloor({
            buildingFloorId,
            floorName,
          } as BuildingFloorItem);
          setSelectedSpace({
            buildingSpaceId,
            spaceName,
          } as BuildingSpaceItem);
        }
      } else {
        setSelectedFloor({
          buildingFloorId: vote.buildingFloorId ?? user.buildingFloorId ?? "",
          floorName: vote.buildingFloorName ?? user.buildingFloorName ?? "",
        } as BuildingFloorItem);
        setSelectedSpace({
          buildingSpaceId: vote.buildingSpaceId ?? user.buildingSpaceId ?? "",
          spaceName: vote.buildingSpaceName ?? user.buildingSpaceName ?? "",
        } as BuildingSpaceItem);
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e?.response?.data?.error.replace(". ", ".\n"));
      } else {
        open("투표 조회를 실패했습니다..\n관리자에게 문의해주세요.");
      }
    } finally {
      if (showIsLoading) setIsVoteLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (
      !selectedFloor?.buildingFloorId ||
      !selectedSpace?.buildingSpaceId ||
      !selectedTime.value
    ) {
      return;
    }

    const isExistVoteId = Boolean(vote.voteId);

    await loadSpaceDetail({
      buildingSpaceId: selectedSpace.buildingSpaceId,
      params: {
        votePeriod: selectedTime.value as VotePeriod,
      },
      isLoadVote: !isExistVoteId,
    });

    if (isExistVoteId) {
      await loadVote(vote);
    }
  };

  const isSkeletonDisplay = () => {
    return isBuildingLoading || popup === "differentBuilding";
  };

  const isContentDisplay = () => {
    return (
      selectedFloor &&
      selectedSpace &&
      selectedTime &&
      !isBuildingLoading &&
      !isVoteLoading &&
      popup !== "differentBuilding"
    );
  };

  const isSpaceDisplay = () => {
    if (!selectedSpace?.buildingSpaceId) return true;

    return (
      selectedSpace &&
      spaceList.length > 0 &&
      spaceList.some(
        (item) => item.buildingSpaceId === selectedSpace.buildingSpaceId
      )
    );
  };

  const resetVoteData = () => {
    setSpaceDetail(undefined);
    setAirSpaceInfo(undefined);
    setVote({});
  };

  useEffect(() => {
    if (user.isLoading || isVoteLoading || isBuildingLoading) return;
    if (!vote.buildingId) return;
    if (!user.buildingId) return;

    if (user.buildingId !== vote.buildingId) {
      setPopup("differentBuilding");
    }
  }, [user.isLoading, vote.buildingId, user.buildingId]);

  useEffect(() => {
    if (!selectedFloor?.buildingFloorId) return;
    if (!user.buildingId) return;

    if (selectedFloor.buildingFloorId) {
      setIsBuildingLoading(true);
      getBuildingSpaceList({
        buildingId: user.buildingId,
        buildingFloorId: selectedFloor.buildingFloorId,
      })
        .then((res) => {
          if (res.status === HttpStatusCode.Ok) {
            setSpaceList(
              res.data?.map((item) => ({
                ...item,
              }))
            );
          }
        })
        .finally(() => {
          setIsBuildingLoading(false);
        });
    }
  }, [selectedFloor?.buildingFloorId, user.buildingId]);

  useEffect(() => {
    if (user.isLoading) return;

    if (user.role?.includes(MANAGER_ROLE.USER)) {
      navigate("/view");
    } else {
      const apiList: Promise<void>[] = [loadNotice(), loadFloor()];
      if (paramVoteId) {
        apiList.push(loadSpaceDetail({ buildingSpaceId: paramSpaceId ?? "" }));
        apiList.push(loadVote({ voteId: paramVoteId }));
      } else {
        apiList.push(
          loadSpaceDetail({
            buildingSpaceId: paramSpaceId ?? "",
            isLoadVote: true,
          })
        );
      }

      setIsLoading(true);
      setIsVoteLoading(true);
      Promise.allSettled(apiList).finally(() => {
        setIsLoading(false);
        setIsVoteLoading(false);
      });
    }
  }, [user.isLoading]);

  return (
    <div>
      {loadingComponent}
      <StickyHeader
        title="더워요 추워요"
        className="px-5"
        onBackClick={() => navigate("/manager/manager-hot-cold-vote")}
        rightContent={
          <div onClick={handleRefresh}>
            <RefreshIcon />
          </div>
        }
      />

      {vote.buildingId && vote.buildingId !== user.buildingId && (
        <DifferentBuildingPopup popup={popup} />
      )}

      <div className="flex flex-col px-5">
        {hotColdNotice && (
          <div
            className="rounded-[3px] py-3 pl-[15px] pr-[20px] bg-[#F7F7F7] text-brand-primary-gray-100 text-xs flex items-center gap-2.5 justify-between"
            onClick={() => navigate(`/talk/${hotColdNotice.lifePostId}`)}
          >
            <div className="flex items-center gap-1">
              <HomeBuildingNoticeIcon fill="#EA27C2" />
              <div className="truncate w-[calc(100vw-100px)]">
                {hotColdNotice.title}
              </div>
              <ViewNoticeLinkIcon stroke="black" />
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2.5 pt-5 pb-5 text-[26px] font-medium">
          <div className="flex gap-2.5">
            {isSkeletonDisplay() || !user.buildingName ? (
              <Skeleton className="h-[38px] w-[200px] rounded-lg" />
            ) : (
              <span className="leading-[38px] truncate">
                {user.buildingName}
              </span>
            )}
            {isSkeletonDisplay() ? (
              <Skeleton className="h-[38px] w-[100px] rounded-lg" />
            ) : (
              <CommonSelect
                optionList={floorList.map((floor) => ({
                  value: floor.buildingFloorId,
                  label: floor.floorName,
                }))}
                defaultValue="층 선택"
                labelClassName="text-brand-primary-magenta truncate"
                onChange={(option) => {
                  const findItem = floorList.find(
                    (item) => item.buildingFloorId === option.value
                  );
                  setSelectedFloor(findItem);
                  setSelectedSpace(undefined);
                  setSelectedTime(timeOption[0]);
                  resetVoteData();
                }}
                value={selectedFloor?.buildingFloorId}
                wrapperClassName="max-w-[110px]"
                arrowIcon={<TalkFindBuildingDownArrow />}
                onClick={() => {
                  removeError("selectFloor");
                  if (floorList.length === 0) {
                    open?.("선택 가능한 층이 없습니다.");
                    return;
                  }
                }}
                optionOrder="between"
                variant="selectBuilding"
                unUsePadding
                error={errors["selectFloor"]}
                ref={selectFloorRef}
              />
            )}
          </div>

          <div className="flex flex-wrap gap-1.5">
            {isSkeletonDisplay() || !isSpaceDisplay() ? (
              <Skeleton className="h-[38px] w-[250px] rounded-lg" />
            ) : (
              <>
                <CommonSelect
                  optionList={spaceList.map((floor) => ({
                    value: floor.buildingSpaceId,
                    label: floor.spaceName,
                  }))}
                  defaultValue="상세공간 선택"
                  labelClassName="text-brand-primary-magenta"
                  onChange={(option) => {
                    const findItem = spaceList.find(
                      (item) => item.buildingSpaceId === option.value
                    );
                    if (!findItem) return;

                    setSelectedSpace(findItem);
                    resetVoteData();

                    if (selectedTime?.value) {
                      loadSpaceDetail({
                        buildingSpaceId: findItem.buildingSpaceId,
                        params: {
                          votePeriod: selectedTime.value as VotePeriod,
                        },
                        isLoadVote: true,
                      });
                    }
                  }}
                  value={selectedSpace?.buildingSpaceId}
                  arrowIcon={<TalkFindBuildingDownArrow />}
                  onClick={() => {
                    removeError("selectSpace");
                    if (spaceList.length === 0) {
                      open?.("선택 가능한 상세공간이 없습니다.");
                      return;
                    }
                  }}
                  optionOrder="between"
                  optionWidth="240px"
                  wrapperClassName="w-fit"
                  variant="selectBuilding"
                  unUsePadding
                  error={errors["selectSpace"]}
                  ref={selectSpaceRef}
                />

                <span className="leading-[38px] font-light">{`의 ${getVoteStatusLabel(
                  vote.status
                )}`}</span>
              </>
            )}

            {isSkeletonDisplay() ? (
              <Skeleton className="h-[38px] w-[100px] rounded-lg" />
            ) : (
              <CommonSelect
                value={selectedTime?.value}
                onChange={(option) => {
                  setSelectedTime(option);
                  resetVoteData();

                  if (selectedSpace?.buildingSpaceId) {
                    loadSpaceDetail({
                      buildingSpaceId: selectedSpace.buildingSpaceId,
                      params: { votePeriod: option.value as VotePeriod },
                      isLoadVote: true,
                    });
                  }
                }}
                optionList={timeOption}
                arrowIcon={<TalkFindBuildingDownArrow />}
                labelClassName="text-brand-primary-magenta"
                optionOrder="between"
                wrapperClassName="w-fit"
                variant="selectBuilding"
                unUsePadding
                error={errors["selectSpace"]}
                ref={selectSpaceRef}
              />
            )}
          </div>
        </div>

        <PinchZoomImage
          imageList={
            spaceDetail?.planFile?.items
              ?.filter((item) => item.scale === "full")
              .map((file) => file.url) ?? []
          }
          isOpen={isModalOpen}
          onClose={(e: React.MouseEvent) => {
            e.stopPropagation(); // 이벤트 버블링을 중단합니다.
            setIsModalOpen(false);
          }}
        />

        {(spaceDetail?.planFile?.items?.length ?? 0) > 0 && (
          <img
            className="max-h-40 pb-[15px] pt-5"
            src={
              spaceDetail?.planFile?.items?.find(
                (item) => item.scale === "full"
              )?.url
            }
            alt="space-plan-file"
            onClick={() => {
              setIsModalOpen(true);
            }}
          />
        )}

        {!spaceDetail && isLoading ? (
          <Skeleton className="w-[calc(100vw-40px)] h-[84px] rounded-lg" />
        ) : (
          <HotColdSummaryList
            buildingSpace={{
              temperature: spaceDetail?.temperature,
              humidity: spaceDetail?.humidity,
              equipmentStatus: spaceDetail?.equipmentStatus,
              ventilationStatus: spaceDetail?.ventilationStatus,
            }}
            isManager
          />
        )}
      </div>
      <HorizontalRule thickness={6} className="bg-brand-neutral-05" />
      <div
        className="flex flex-col gap-2.5"
        data-testid="manager-hot-cold-vote-detail-content"
      >
        {isContentDisplay() && (
          <>
            <div className="flex flex-col gap-[30px] px-5">
              {vote.location && (
                <div className="flex flex-col gap-5">
                  <div className="flex justify-center text-[26px] items-center pt-1.5 gap-2">
                    <span className="font-light min-w-fit">세부 위치 :</span>
                    <span className="font-medium truncate">
                      {vote.location}
                    </span>
                  </div>
                  <HorizontalRule className="bg-black" margin="0" />
                </div>
              )}
              <div className="flex flex-col">
                {vote.status === VOTE_STATUS.COMPLETED && (
                  <>
                    <HotColdLabelWrapper
                      label={vote.feedbackAt ? "피드백" : "피드백 등록하기"}
                      className="pb-[30px]"
                    >
                      <HotColdFeedback
                        vote={{
                          feedback: vote.feedback,
                          voteId: vote.voteId,
                          buildingSpaceId: vote.buildingSpaceId,
                          isSatisfactionRegistered:
                            vote.isSatisfactionRegistered,
                        }}
                        editMode={Boolean(vote.feedbackAt)}
                        voteItemType={vote.result as VoteItemType}
                        open={open}
                        setIsLoading={setIsLoading}
                        onRefresh={handleRefresh}
                      />
                    </HotColdLabelWrapper>
                  </>
                )}

                {vote.status !== undefined && (
                  <>
                    {vote.creatorVoteItem && (
                      <HotColdLabelWrapper
                        label={`${vote.createdByNickname}님은`}
                        className="pb-[30px]"
                      >
                        <div className="flex flex-col gap-2.5">
                          <VoteButton
                            type="VOTED"
                            voteItemType={vote.creatorVoteItem?.itemType}
                            children={
                              <div className="flex justify-center items-center">
                                <span
                                  className={getTextColor(
                                    vote.creatorVoteItem?.itemType
                                  )}
                                >
                                  {getVoteItemTypeLabel(
                                    vote.creatorVoteItem?.itemType
                                  )}
                                </span>
                                에 투표하셨어요
                              </div>
                            }
                          />
                        </div>
                      </HotColdLabelWrapper>
                    )}
                    <HotColdLabelWrapper
                      label={`${selectedTime.label} 이용자 반응`}
                      className="pb-5"
                      bottomLabel={
                        <div>
                          <HotColdVoteRemainTime
                            status={vote.status}
                            endTime={vote.endedAt}
                            prefix={"투표 종료 : "}
                            onRefresh={() => loadVote(vote, false)}
                          />
                        </div>
                      }
                    >
                      <HotColdVoteBarChart
                        voteSummary={vote?.summary}
                        showLabel
                        size="medium"
                      />
                    </HotColdLabelWrapper>
                  </>
                )}
              </div>
            </div>

            <HotColdLabelWrapper
              label="실내 온/습도 변화"
              className="px-5 pb-2.5"
            >
              {isLoading ? (
                <Skeleton className="h-[200px] w-[calc(100vw-40px)] rounded-lg" />
              ) : (
                <>
                  {airSpaceInfo?.graphData?.some(
                    (item) => item.temperature && item.humidity
                  ) ? (
                    <HotColdVoteLineChart graphData={airSpaceInfo.graphData} />
                  ) : (
                    <HotColdVoteTempDisplay
                      label="온/습도 데이터가 존재하지 않습니다."
                      className="min-h-[80px]"
                    />
                  )}
                </>
              )}
            </HotColdLabelWrapper>

            <HotColdLabelWrapper
              className="bg-[#F7F7F7] py-[30px] px-5"
              label="평균 층 온도(실내)"
              rightContent={
                <span className="text-brand-neutral-50 text-xs">
                  (최근 3일간)
                </span>
              }
            >
              {isLoading ? (
                <Skeleton className="h-[200px] w-[calc(100vw-40px)] rounded-lg" />
              ) : (
                <>
                  {airSpaceInfo?.averageTemperature ? (
                    <div className="flex gap-2.5 justify-center">
                      <HotColdVoteTempDisplay
                        label="평균 온도"
                        value={
                          airSpaceInfo?.averageTemperature?.average ?? DASH
                        }
                      />
                      <HotColdVoteTempDisplay
                        label="최저 평균"
                        value={
                          airSpaceInfo?.averageTemperature?.averageMin ?? DASH
                        }
                      />
                      <HotColdVoteTempDisplay
                        label="최고 평균"
                        value={
                          airSpaceInfo?.averageTemperature?.averageMax ?? DASH
                        }
                      />
                    </div>
                  ) : (
                    <HotColdVoteTempDisplay
                      label="온도 데이터가 존재하지 않습니다."
                      className="min-h-[80px]"
                    />
                  )}
                </>
              )}
            </HotColdLabelWrapper>
          </>
        )}
      </div>

      {simplePopupComponent}
    </div>
  );
}

export type HotColdFeedbackProps = {
  vote: Partial<ManagerVote>;
  editMode?: boolean;
  voteItemType?: VoteItemType;
  open: (message: string) => void;
  setIsLoading?: (value: boolean) => void;
  onRefresh?: () => void;
};
export const HotColdFeedback = ({
  vote,
  editMode,
  voteItemType,
  open,
  setIsLoading,
  onRefresh,
}: HotColdFeedbackProps) => {
  const hotFeedbackList = [
    "샌디 매니저가 온도를 조절하였습니다. 소중한 의견 감사합니다. 조금만 기다려주세요. 곧 따뜻해 질 거예요^^",
    "샌디 매니저가 온도를 조절하였습니다. 소중한 의견 감사합니다. 쾌적한 공간에서 오늘도 힘내세요!",
    "샌디 매니저가 난방을 시작하였습니다. 조금만 기다려주세요. 곧 따뜻해 질 거예요^^",
    "샌디 매니저가 난방을 시작하였습니다. 쾌적한 공간에서 오늘도 힘내세요!",
    "현재 최대 난방 중이예요. 조금씩 따뜻해지는 중입니다^^",
    "소중한 지구를 위해 적정온도로 유지하고 있습니다. 함께 참여해주세요^^",
    "시원한 여름을 위한 냉방기 휴식기간입니다 : )",
    "따뜻한 겨울을 위한 난방기 휴식기간입니다 : )",
  ];

  const coldFeedbackList = [
    "샌디 매니저가 온도를 조절하였습니다. 소중한 의견 감사합니다. 조금만 기다려주세요. 곧 시원해질거에요^^",
    "샌디 매니저가 온도를 조절하였습니다. 소중한 의견 감사합니다. 쾌적한 공간에서 오늘도 힘내세요!",
    "현재 최대 냉방 중이에요. 쾌적한 온도제공을 위해 더 노력하겠습니다.",
    "소중한 지구를 위해 적정온도로 유지하고 있습니다. 함께 참여해주세요^^",
    "냉방병 예방을 위해 적정온도로 유지 중입니다. 소중한 여러분의 건강을 위해 조금만 참아주세요.",
    "업무에 집중하고 계시군요! 일하기 좋은 온도로 하향조절 했어요 : )",
    "시원한 여름을 위한 냉방기 휴식기간입니다 : )",
    "따뜻한 겨울을 위한 난방기 휴식기간입니다 : )",
  ];

  const goodFeedbackList = ["함께하는 동료들이 온도에 만족하였습니다."];

  const navigate = useNavigate();

  const [content, setContent] = useState<string>(vote.feedback ?? "");
  const [popup, setPopup] = useState<"feedback" | "userSatisfaction" | "">("");
  const [feedbackIndex, setFeedbackIndex] = useState<number>(-1);

  const getOption = () => {
    switch (voteItemType) {
      case VOTE_ITEM_TYPE.HOT:
        return coldFeedbackList;
      case VOTE_ITEM_TYPE.COLD:
        return hotFeedbackList;
      case VOTE_ITEM_TYPE.GOOD:
        return goodFeedbackList;
      default:
        return [];
    }
  };

  const actionWord = editMode ? "수정" : "등록";

  const resetFeedbackInPopup = () => {
    if (editMode) {
      setFeedbackIndex(-1);
      setContent(vote.feedback ?? "");
    }
  };

  return (
    <div className="flex flex-col gap-2.5">
      {!editMode ? (
        <HotColdVoteFeedbackForm
          feedback={content}
          optionList={getOption()}
          onChange={setContent}
          feedbackIndex={feedbackIndex}
          onFeedbackIndexChange={setFeedbackIndex}
        />
      ) : (
        <>
          {vote?.feedback && (
            <div className="border border-brand-neutral-20 rounded-[3px] text-sm font-normal break-words break-keep p-3 text-brand-neutral-90">
              {vote.feedback}
            </div>
          )}
        </>
      )}
      <CommonButton
        variant="primary"
        disabled={(content.trim()?.length ?? 0) < 10}
        className="px-4 py-[11px] !font-semibold !h-[50px]"
        onClick={() => {
          if (!vote.voteId) return;

          if (vote.isSatisfactionRegistered) {
            navigate(`/manager/manager-hot-cold-vote/feedback/${vote.voteId}`, {
              state: { ...vote },
            });
          } else {
            if ((content.trim()?.length ?? 0) < 0) {
              open?.("피드백은 10자 이상 입력해주세요.");
            } else {
              setPopup("feedback");
            }
          }
        }}
      >
        {vote.isSatisfactionRegistered
          ? "사용자 만족도 확인"
          : `피드백 ${actionWord}`}
      </CommonButton>

      <CommonPopup
        isOpen={popup === "feedback"}
        padding={0}
        onClose={() => {
          setPopup("");
          resetFeedbackInPopup();
        }}
      >
        <div
          className={clsx("whitespace-pre-line px-5 text-center font-medium", {
            "py-5": editMode,
            "py-10": !editMode,
          })}
        >
          {editMode ? (
            <div className="flex flex-col gap-4">
              <span className="text-xl font-semibold">피드백 수정하기</span>
              <HotColdVoteFeedbackForm
                feedback={content}
                optionList={getOption()}
                onChange={setContent}
                feedbackIndex={feedbackIndex}
                onFeedbackIndexChange={setFeedbackIndex}
              />
            </div>
          ) : (
            `피드백을 ${actionWord} 하시겠습니까?`
          )}
        </div>
        <div className="flex">
          <CommonButton
            className="w-full rounded-none h-bottom-button"
            variant="secondary"
            onClick={() => {
              setPopup("");

              resetFeedbackInPopup();
            }}
          >
            취소
          </CommonButton>
          <CommonButton
            variant="primary"
            disabled={content.length < 10}
            className="w-full rounded-none h-bottom-button"
            onClick={async () => {
              if (!vote.voteId) return;

              if (content.length < 10) {
                open?.("피드백은 10자 이상 입력해주세요.");
                return;
              }

              try {
                const processFeedback = editMode
                  ? patchManagerHotColdVoteFeedback
                  : postManagerHotColdVoteFeedback;

                const res = await processFeedback({
                  voteId: vote.voteId,
                  feedback: content,
                });

                if (res.status === HttpStatusCode.Ok) {
                  open(`피드백이 ${actionWord} 되었습니다.`);
                }
              } catch (e) {
                const errorMessage = `${actionWord}을 실패했습니다.\n관리자에게 문의해주세요.`;
                errorResponse(e, open, errorMessage);
              } finally {
                setIsLoading?.(false);
                onRefresh?.();
                setPopup("");
              }
            }}
          >
            {actionWord}
          </CommonButton>
        </div>
      </CommonPopup>
    </div>
  );
};

export type HotColdVoteFeedbackFormProps = {
  optionList: string[];
  feedback: string;
  feedbackIndex: number;
  onChange: (option: string) => void;
  onFeedbackIndexChange: (index: number) => void;
};

export const HotColdVoteFeedbackForm = ({
  optionList,
  feedback,
  feedbackIndex,
  onChange,
  onFeedbackIndexChange,
}: HotColdVoteFeedbackFormProps) => {
  return (
    <div className="flex flex-col gap-5">
      <div className=" rounded-[10px] max-h-[220px] bg-brand-neutral-05 flex flex-col gap-2 py-[15px] pl-[15px] pr-[5px]">
        <SimpleScrollBar style={{ maxHeight: "190px" }} autoHide={false}>
          <div className="flex flex-col gap-2 pr-[9px]">
            {optionList.map((option, index) => (
              <div
                key={index}
                className={clsx([
                  "flex justify-center items-center px-2 py-3 text-sm border bg-white break-keep rounded-[3px]",
                  {
                    " border-brand-primary-magenta": index === feedbackIndex,
                  },
                ])}
                onClick={() => {
                  if (feedbackIndex !== index) {
                    onChange(option);
                    onFeedbackIndexChange(index);
                  } else {
                    onChange("");
                    onFeedbackIndexChange(-1);
                  }
                }}
              >
                {option}
              </div>
            ))}
          </div>
        </SimpleScrollBar>
      </div>
      <CommonTextArea
        value={feedback}
        onClick={() => onFeedbackIndexChange(-1)}
        onChange={(e) => {
          onChange(e.target.value);
          onFeedbackIndexChange(-1);
        }}
        inputClassName="text-sm h-[90px] break-keep"
        placeholder={"피드백 직접 작성하기\n(10자 이상)"}
      />
    </div>
  );
};

export type HotColdVoteTempDisplayProps = {
  label?: React.ReactNode;
  value?: React.ReactNode;
  className?: string;
};

export const HotColdVoteTempDisplay = ({
  label,
  value,
  className,
}: HotColdVoteTempDisplayProps) => {
  return (
    <div
      className={clsx(
        "flex flex-col gap-2.5 flex-1 h-[110px] bg-white break-keep text-center font-semibold justify-center items-center px-4 py-[11px] rounded-[10px]",
        className
      )}
      style={{ boxShadow: "0px 2px 5px 0px rgba(0, 0, 0, 0.1)" }}
    >
      <span className="text-brand-neutral-50 text-sm leading-[17px] font-medium">
        {label}
      </span>
      {value && (
        <span className="text-[24px] leading-[29px]">
          {value}
          <span className="text-base leading-[19px] align-text-top">℃</span>
        </span>
      )}
    </div>
  );
};
